import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createAgitem,
  fetchAllAgitems,
  fetchOneAgitem,
  updateAgitem,
  deleteAgitem,
  searchAllAgitems,
  searchAllAgitemsWithoutSize,
  searchItemsForAsyncSelect,
  fetchItemsGroupedForSelect,
  fetchItemDetailsWithSizes,
  fetchAgitemDetailsWithPricelist,
  fetchBrandsByVendor,
  getSingleItemByFilter,
  bulkUpdatePackRatios,
  generateItemListReport,
} from "services/agitem";

export const useAllAgitems = (filters = {}, enabled = true) => {
  const query = useQuery({
    queryKey: ["all-agitems", filters],
    queryFn: () => fetchAllAgitems(filters),
    enabled,
  });
  return query;
};

export const useSearchAllAgitems = (filters = {}) => {
  const query = useQuery({
    queryKey: ["search-all-agitems", filters],
    queryFn: () => searchAllAgitems(filters),
  });
  return query;
};

export const useSearchAllAgitemsWithoutSize = (filters = {}) => {
  const query = useQuery({
    queryKey: ["search-all-agitems-without-size", filters],
    queryFn: () => searchAllAgitemsWithoutSize(filters),
  });
  return query;
};

export const useOneAgitem = (id, enabled = true) => {
  const query = useQuery({
    queryKey: ["one-agitem", id],
    queryFn: () => {
      if (id) {
        return fetchOneAgitem(id);
      }
    },
    enabled,
  });
  return query;
};

export const useCreateAgitem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    queryKey: ["create-agitem"],
    mutationFn: (data) => createAgitem(data),
    onSuccess: (data, variables) => {
      // Invalidate all agitem-related queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ["all-agitems"] });
      queryClient.invalidateQueries({ queryKey: ["search-all-agitems"] });
      queryClient.invalidateQueries({
        queryKey: ["search-all-agitems-without-size"],
      });
      queryClient.invalidateQueries({ queryKey: ["items-grouped-for-select"] });
      queryClient.invalidateQueries({
        queryKey: ["search-items-async-select"],
      });

      // Invalidate brand-vendor relationships if vendor was used
      if (variables.itmvendorId) {
        queryClient.invalidateQueries({ queryKey: ["brands-by-vendor"] });
      }
    },
  });
};

export const useUpdateAgitem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    queryKey: ["update-agitem"],
    mutationFn: (data) => updateAgitem(data),
    onSuccess: (data, variables) => {
      // Invalidate all agitem-related queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ["all-agitems"] });
      queryClient.invalidateQueries({ queryKey: ["search-all-agitems"] });
      queryClient.invalidateQueries({
        queryKey: ["search-all-agitems-without-size"],
      });
      queryClient.invalidateQueries({ queryKey: ["items-grouped-for-select"] });
      queryClient.invalidateQueries({
        queryKey: ["search-items-async-select"],
      });
      // Invalidate all one-agitem queries
      queryClient.invalidateQueries({ queryKey: ["one-agitem"] });
      // Also invalidate the specific item that was updated
      if (variables?.id) {
        queryClient.invalidateQueries({ queryKey: ["one-agitem", variables.id] });
      }
    },
  });
};

export const useDeleteAgitem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteAgitem(id),
    onSuccess: (data, variables) => {
      // Invalidate agitem queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ["all-agitems"] });
      queryClient.invalidateQueries({ queryKey: ["one-agitem"] });
    },
  });
};

export const useSearchItemsForAsyncSelect = (
  { query, vendorId = null },
  options = {}
) => {
  return useQuery({
    queryKey: ["search-items-async-select", query, vendorId],
    queryFn: () => searchItemsForAsyncSelect({ query, vendorId }),
    enabled: query && query.trim().length >= 3, // Only run if query has 3+ characters
    staleTime: 30000, // Cache results for 30 seconds
    ...options,
  });
};

export const useItemsGroupedForSelect = (
  { vendorId = null } = {},
  options = {}
) => {
  return useQuery({
    queryKey: ["items-grouped-for-select", vendorId],
    queryFn: () => fetchItemsGroupedForSelect({ vendorId }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const useItemDetailsWithSizes = (options = {}) => {
  return useMutation({
    mutationFn: (itemId) => fetchItemDetailsWithSizes(itemId),
    ...options,
  });
};

export const useAgitemDetailsWithPricelist = (params = {}, enabled = true) => {
  return useQuery({
    queryKey: ["agitem-details-with-pricelist", params],
    queryFn: () => fetchAgitemDetailsWithPricelist(params),
    enabled,
  });
};

export const useBrandsByVendor = (vendorId, enabled = true) => {
  return useQuery({
    queryKey: ["brands-by-vendor", vendorId],
    queryFn: () => fetchBrandsByVendor(vendorId),
    enabled: Boolean(vendorId) && enabled,
  });
};

export const useSingleItemByFilter = (options = {}) => {
  return useMutation({
    mutationFn: (params) => getSingleItemByFilter(params),
    ...options,
  });
};

export const useBulkUpdatePackRatios = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => bulkUpdatePackRatios(data),
    onSuccess: (data, variables) => {
      // Invalidate agitem and agitemdetail queries since pack ratios affect item data
      queryClient.invalidateQueries({ queryKey: ["all-agitemdetails"] });
      queryClient.invalidateQueries({ queryKey: ["complete-agitemdetails"] });
      queryClient.invalidateQueries({ queryKey: ["one-agitem"] });
      queryClient.invalidateQueries({ queryKey: ["all-agitems"] });
      queryClient.invalidateQueries({ queryKey: ["items-grouped-for-select"] });
    },
  });
};

export const useGenerateItemListReport = () => {
  return useMutation({
    mutationFn: (params) => generateItemListReport(params),
  });
};
